import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "../../i18n";
import { withPrices } from "./menu-edit";
import type { MenuEditor } from "./useMenuEditor";

export interface QuickPrices {
  /** Prices typed in the list that are being saved (by `priceKey`): the list shows them already. */
  pending: ReadonlyMap<string, number>;
  /** A price typed in the list: it is saved at once, together with the ones typed while saving. */
  set: (key: string, price: number) => void;
}

/**
 * Prices straight in the list of Menu & prijzen (Furkan, 09/10/2026: "direct sayinin ustine tiklayip
 * degistirip enterlasak sonrakine gecse hizli hizli"). Every price is a new version of the menu, active at
 * once on every device, as every change in Menu & prijzen.
 *
 * Typing goes faster than saving: the price that follows is typed while the one before is still on its
 * way. A version of the menu can only be saved on the version before it, so the prices typed meanwhile
 * wait, and go together as soon as that one is stored, on the menu as it is then. Nothing typed is lost
 * and nothing is saved on an old version.
 */
export function useQuickPrices(editor: MenuEditor): QuickPrices {
  const [pending, setPending] = useState<ReadonlyMap<string, number>>(() => new Map());
  const saving = useRef(false);
  const { menu, busy, commit, toast } = editor;

  useEffect(() => {
    if (busy || saving.current || pending.size === 0) return;
    const batch = new Map(pending);
    const { menu: next, changed } = withPrices(menu, batch);
    /** What this save took along leaves the waiting prices; a price typed again meanwhile stays. */
    const done = () => {
      setPending((current) => {
        const rest = new Map(current);
        for (const [key, price] of batch) if (rest.get(key) === price) rest.delete(key);
        return rest;
      });
    };
    if (changed.length === 0) {
      done();
      return;
    }
    saving.current = true;
    void commit(next, t.menuAdmin.prices.log(changed)).then((saved) => {
      saving.current = false;
      done();
      if (!saved) toast(t.menuAdmin.prices.notSaved(changed), "bad");
    });
  }, [busy, menu, commit, toast, pending]);

  const set = useCallback((key: string, price: number) => {
    setPending((current) => new Map(current).set(key, price));
  }, []);

  return { pending, set };
}
